import React from "react";

import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";

import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";

import InlineTags from "@auto/widgets/InlineTags";

import {
  readUserAccountRequest,
  approveUserAccountRequest,
  rejectUserAccountRequest,
} from "@src/lib/api.js";
import USER_ACCOUNT_REQUEST_TAG_TYPES from "@src/metadata/useraccountrequesttagtypes.mjs";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

import styles from "./UserAccountRequestForm.module.styl";

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value.replace("T", " ").slice(0, 19);
  }

  try {
    return new Date(value).toISOString().replace("T", " ").slice(0, 19);
  } catch {
    return String(value);
  }
};

const getField = (row, camelName, snakeName) => {
  return row?.[camelName] ?? row?.[snakeName] ?? "";
};

const fullName = (request) => {
  const firstName = getField(request, "firstName", "first_name");
  const lastName = getField(request, "lastName", "last_name");

  return `${firstName} ${lastName}`.trim();
};

const normalizeStatus = (value) => {
  return String(value || "").trim().toUpperCase();
};

const getStatusMeta = (status) => {
  switch (normalizeStatus(status)) {
    case "READY":
      return {
        label: t("common.ready"),
        className: styles.statusReady,
        Icon: FactCheckOutlinedIcon,
      };

    case "ERROR":
      return {
        label: t("common.error"),
        className: styles.statusError,
        Icon: ErrorOutlineOutlinedIcon,
      };

    case "APPROVED":
      return {
        label: t("common.approved"),
        className: styles.statusApproved,
        Icon: CheckCircleOutlineIcon,
      };

    case "REJECTED":
      return {
        label: t("common.rejected"),
        className: styles.statusRejected,
        Icon: HighlightOffIcon,
      };

    case "PENDING":
    default:
      return {
        label: t("common.pending"),
        className: styles.statusPending,
        Icon: PendingActionsOutlinedIcon,
      };
  }
};

const normalizeTags = (tags) => {
  if (!tags) {
    return {};
  }

  if (typeof tags === "string") {
    try {
      const parsed = JSON.parse(tags);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  if (typeof tags !== "object" || Array.isArray(tags)) {
    return {};
  }

  return tags;
};

const hasTags = (tags) => Object.keys(normalizeTags(tags)).length > 0;

const pickDecisionTime = (request) => {
  const approvedAt = formatDateTime(getField(request, "approvedAt", "approved_at"));
  const rejectedAt = formatDateTime(getField(request, "rejectedAt", "rejected_at"));
  const reviewedAt = formatDateTime(getField(request, "reviewedAt", "reviewed_at"));

  return approvedAt || rejectedAt || reviewedAt || "";
};

const votingCenterLabel = (votingCenter) => {
  if (!votingCenter) {
    return "";
  }

  const code = String(votingCenter.code || "").trim();
  const name = String(votingCenter.name || "").trim();
  const address = String(votingCenter.address || "").trim();

  const base =
    code && name
      ? `${code} - ${name}`
      : name || code || "";

  return address && base ? `${base} — ${address}` : base || address;
};

const readByGid = async (entity, gid) => {
  if (!gid) {
    return null;
  }

  return await rest.request(getServiceUri() + `${entity}/${gid}`, "GET");
};

const escapeRsqlString = (value) => {
  return String(value || "")
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"');
};

const findPersonByNinu = async (ninu) => {
  const normalized = String(ninu || "").trim();
  if (!normalized) {
    return null;
  }

  const rows = await rest.request(
    getServiceUri() + "person/search",
    "POST",
    {
      columns: [],
      query: `nationalIdentifier=="${escapeRsqlString(normalized)}"`,
      limit: 1,
      offset: 0,
    }
  );

  return Array.isArray(rows) && rows.length ? rows[0] : null;
};

const findPersonPhoto = async (person) => {
  const personGid = person?.gid ?? person?.personGid ?? person?.person_gid;
  if (!personGid) {
    return null;
  }

  const rows = await rest.request(
    getServiceUri() + "person-photo/search",
    "POST",
    {
      columns: [],
      query: `personGid==${Number(personGid)}`,
      limit: 50,
      offset: 0,
    }
  );

  if (!Array.isArray(rows) || !rows.length) {
    return null;
  }

  return rows.find((photo) => photo?.isDefault === true || photo?.is_default === true) || rows[0];
};

const personPhotoUrl = (photo) => {
  if (!photo?.id) {
    return "";
  }

  return `${getServiceUri()}person-photo/data/${Number(photo.id)}`;
};

const personRecordUrl = (person) => {
  const id = person?.gid || person?.id;
  return id ? `/person/${id}` : "";
};

const enrichUserAccountRequest = async (request, gui) => {
  if (!request) {
    return null;
  }

  const votingCenterId = getField(request, "votingCenterId", "voting_center_id");

  const nationalId = getField(request, "nationalId", "national_id");

  const [votingCenter, person] = await Promise.all([
    votingCenterId
      ? readByGid("voting-center", votingCenterId)
      : Promise.resolve(null),
    nationalId && can(gui, "READ_PERSON")
      ? findPersonByNinu(nationalId)
      : Promise.resolve(null),
  ]);

  const personPhoto =
    person
    && can(gui, "SEARCH_PERSON_PHOTO")
    && can(gui, "READ_PERSON_PHOTO")
      ? await findPersonPhoto(person)
      : null;

  return {
    ...request,
    tags: normalizeTags(getField(request, "tags", "tags")),
    votingCenterId,
    votingCenter,
    votingCenterLabel: votingCenterLabel(votingCenter),
    person,
    personPhoto,
    personPhotoUrl: personPhotoUrl(personPhoto),
    personRecordUrl: personRecordUrl(person),
  };
};

const StatusPill = ({ status }) => {
  const meta = getStatusMeta(status);

  return (
    <div className={`${styles.statusPill} ${meta.className}`}>
      <meta.Icon className={styles.statusPillIcon} />
      <span>{meta.label}</span>
    </div>
  );
};

const RequestTags = ({ tags }) => {
  const normalized = normalizeTags(tags);

  if (!Object.keys(normalized).length) {
    return null;
  }

  return (
    <InlineTags
      tags={normalized}
      tagTypes={USER_ACCOUNT_REQUEST_TAG_TYPES}
      disabled
      showHeader={false}
      emptyText=""
      className={styles.inlineTagsInForm}
    />
  );
};

const SummaryCard = ({ icon, label, value, children }) => {
  const Icon = icon;

  return (
    <div className={styles.summaryCard}>
      <div className={styles.summaryIconWrap}>
        <Icon className={styles.summaryIcon} />
      </div>

      <div className={styles.summaryContent}>
        <div className={styles.summaryLabel}>{label}</div>
        <div className={styles.summaryValue}>{children || value || "—"}</div>
      </div>
    </div>
  );
};

const SectionCard = ({ icon, title, subtitle, children }) => {
  const Icon = icon;

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div className={styles.cardTitleRow}>
          <div className={styles.cardIconWrap}>
            <Icon className={styles.cardIcon} />
          </div>

          <div className={styles.cardHeaderText}>
            <h2 className={styles.cardTitle}>{title}</h2>
            {subtitle ? <div className={styles.cardSubtitle}>{subtitle}</div> : null}
          </div>
        </div>
      </header>

      <div className={styles.cardBody}>{children}</div>
    </section>
  );
};

const Field = ({ label, value, children }) => {
  if ((value === null || value === undefined || value === "") && !children) {
    return null;
  }

  return (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>{label}</div>
      <div className={styles.fieldValue}>{children || String(value)}</div>
    </div>
  );
};

const LinkedField = ({ label, value, href, gui }) => {
  if (!value) {
    return null;
  }

  return (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>{label}</div>
      <button
        type="button"
        className={styles.linkValue}
        onClick={() => gui.goTo(href)}
      >
        {String(value)}
      </button>
    </div>
  );
};

const UserAccountRequestHeader = ({
  request,
  saving,
  onApprove,
  onReject,
  canManage,
}) => {
  const status = normalizeStatus(getField(request, "requestStatus", "request_status"));
  const canReview = status === "READY" || status === "ERROR";
  const name = fullName(request) || t("user.accountRequest");

  return (
    <section className={styles.header}>
      <div className={styles.headerMain}>
        <div className={styles.heroIconWrap}>
          <ManageAccountsOutlinedIcon className={styles.heroIcon} />
        </div>

        <div className={styles.headerText}>
          <h1 className={styles.headerTitle}>{name}</h1>

          <div className={styles.headerSubtitle}>
            {t("user.requestGid")}: {request?.gid || "—"}
          </div>

          {hasTags(request?.tags) ? (
            <div className={styles.headerTags}>
              <RequestTags tags={request.tags} />
            </div>
          ) : null}
        </div>
      </div>

      <div className={styles.headerAside}>
        <StatusPill status={status} />

        {canManage && canReview ? (
          <div className={styles.headerActions}>
            <Button
              variant="contained"
              color="success"
              onClick={onApprove}
              disabled={saving}
              className={styles.actionButton}
            >
              {t("user.approve")}
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={onReject}
              disabled={saving}
              className={styles.actionButton}
            >
              {t("user.reject")}
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
};

const UserAccountRequestReadView = ({ gui, id }) => {
  const [request, setRequest] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const loaded = await readUserAccountRequest(id);
      const enriched = await enrichUserAccountRequest(loaded, gui);
      setRequest(enriched);
    } catch (err) {
      setError(err?.message || String(err));
      setRequest(null);
    } finally {
      setLoading(false);
    }
  }, [gui, id]);

  React.useEffect(() => {
    load();
  }, [load]);

  const onApprove = async () => {
    if (!request?.gid) {
      return;
    }

    setSaving(true);
    setError("");

    try {
      await approveUserAccountRequest(request.gid, {});
      await load();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  const onReject = async () => {
    if (!request?.gid) {
      return;
    }

    setSaving(true);
    setError("");

    try {
      await rejectUserAccountRequest(request.gid, {});
      await load();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !request) {
    return <div className={styles.loading}>{t("common.loading")}</div>;
  }

  if (error && !request) {
    return <div className={styles.error}>{error}</div>;
  }

  if (!request) {
    return null;
  }

  const submittedAt = formatDateTime(getField(request, "submittedAt", "submitted_at"));
  const decisionTime = pickDecisionTime(request);
  const reviewerAccountId = getField(request, "reviewerAccountId", "reviewer_account_id");
  const createdUserAccountId = getField(request, "createdUserAccountId", "created_user_account_id");
  const decisionReason = getField(request, "decisionReason", "decision_reason");
  const status = normalizeStatus(getField(request, "requestStatus", "request_status"));

  return (
    <div className={styles.page}>
      <UserAccountRequestHeader
        request={request}
        saving={saving}
        onApprove={onApprove}
        onReject={onReject}
        canManage={can(gui, "MANAGE_USER_ACCOUNT")}
      />

      {error ? <div className={styles.errorInline}>{error}</div> : null}

      <div className={styles.summaryGrid}>
        <SummaryCard
          icon={ScheduleOutlinedIcon}
          label={t("common.submitted")}
          value={submittedAt}
        />

        <SummaryCard
          icon={FactCheckOutlinedIcon}
          label={t("common.status")}
        >
          <StatusPill status={status} />
        </SummaryCard>

        <SummaryCard
          icon={PhoneAndroidOutlinedIcon}
          label={t("common.device")}
          value={getField(request, "deviceId", "device_id")}
        />

        <SummaryCard
          icon={PlaceOutlinedIcon}
          label={t("voter.votingCenter")}
          value={request.votingCenterLabel}
        />
      </div>

      {hasTags(request.tags) ? (
        <SectionCard
          icon={ErrorOutlineOutlinedIcon}
          title={t("common.tags")}
          subtitle={t("user.tagsSubtitle")}
        >
          <RequestTags tags={request.tags} />
        </SectionCard>
      ) : null}

      <div className={styles.gridTwo}>
        <SectionCard
          icon={PersonOutlineOutlinedIcon}
          title={t("voter.applicant")}
          subtitle={t("user.applicantSubtitle")}
        >
          {request.personPhotoUrl ? (
            request.personRecordUrl && can(gui, "READ_PERSON") ? (
              <ButtonBase
                onClick={() => gui.goTo(request.personRecordUrl)}
                sx={{ mb: 2, borderRadius: 2 }}
                aria-label={t("user.openPersonRecord")}
              >
                <Avatar
                  src={request.personPhotoUrl}
                  alt={fullName(request.person) || fullName(request) || t("voter.applicant")}
                  variant="rounded"
                  sx={{ width: 144, height: 144 }}
                />
              </ButtonBase>
            ) : (
              <Avatar
                src={request.personPhotoUrl}
                alt={fullName(request.person) || fullName(request) || t("voter.applicant")}
                variant="rounded"
                sx={{ width: 144, height: 144, mb: 2 }}
              />
            )
          ) : null}

          <div className={styles.fieldsGrid}>
            <Field label={t("person.firstName")} value={getField(request, "firstName", "first_name")} />
            <Field label={t("person.lastName")} value={getField(request, "lastName", "last_name")} />
            <Field label={t("user.username")} value={request.username} />

            {request.personRecordUrl && can(gui, "READ_PERSON") ? (
              <LinkedField
                gui={gui}
                label={t("person.nationalId")}
                value={getField(request, "nationalId", "national_id")}
                href={request.personRecordUrl}
              />
            ) : (
              <Field label={t("person.nationalId")} value={getField(request, "nationalId", "national_id")} />
            )}
          </div>
        </SectionCard>

        <SectionCard
          icon={PlaceOutlinedIcon}
          title={t("voter.votingCenter")}
          subtitle={t("user.votingCenterRequestSubtitle")}
        >
          <div className={styles.fieldsGrid}>
            <Field
              label={t("voter.votingCenter")}
              value={request.votingCenterLabel}
            />
          </div>
        </SectionCard>
      </div>

      <div className={styles.gridTwo}>
        <SectionCard
          icon={BadgeOutlinedIcon}
          title={t("user.createdAccount")}
          subtitle={t("user.createdAccountSubtitle")}
        >
          <div className={styles.fieldsGrid}>
            <Field label={t("user.createdAccount")} value={createdUserAccountId} />
          </div>
        </SectionCard>

        <SectionCard
          icon={SupervisorAccountOutlinedIcon}
          title={t("user.decision")}
          subtitle={t("user.decisionSubtitle")}
        >
          <div className={styles.fieldsGrid}>
            <Field label={t("voter.reviewer")} value={reviewerAccountId} />
            <Field label={t("user.decisionTime")} value={decisionTime} />
            <Field label={t("user.decisionReason")} value={decisionReason} />
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export const displayReadUserAccountRequest = (gui, id) => {
  return {
    uuid: `user-account-request-read-${id}`,
    view: () => <UserAccountRequestReadView gui={gui} id={id} />,
  };
};

export const displayUserAccountRequest = displayReadUserAccountRequest;

export default displayReadUserAccountRequest;
